import { toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import {
  useDeleteSOP,
  useDuplicateSOP,
  useSOPsQuery,
  useToggleSOPStatus,
} from "../api/queries";
import { SOPFilter } from "../ui/SOPFilter";
import { SOPTable } from "../ui/SOPTable";
import { SOPToolbar } from "../ui/SOPToolbar";

export function SOPPage() {
  const nav = useNavigate({ from: "/app/sop" });
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/sop" });

  const list = useSOPsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission(["sop:create"]);
  const toggleStatus = useToggleSOPStatus();
  const deleteSOP = useDeleteSOP();
  const duplicateSOP = useDuplicateSOP();

  return (
    <>
      <Header />

      <Main>
        <SOPToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/sop/new" })}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <SOPFilter />

          <SOPTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onView={(sop) =>
              nav({ to: "/app/sop/$id", params: { id: sop.id } })
            }
            onEdit={(sop) => {
              nav({ to: "/app/sop/$id/edit", params: { id: sop.id } });
            }}
            onDuplicate={async (sop) => {
              const promise = duplicateSOP.run(sop.id);
              toast.promise(promise, {
                loading: "ກໍາລັງຄັດລອກ...",
                success: "ຄັດລອກ SOP ສໍາເລັດ",
                error: "ຄັດລອກ SOP ລົ້ມເຫຼວ",
              });
              const result = await promise;
              // Navigate to edit page of the duplicated SOP
              nav({ to: "/app/sop/$id/edit", params: { id: result.id } });
            }}
            onToggleStatus={(sop) => {
              toggleStatus.mutate(sop.id);
            }}
            onDelete={async (sop) =>
              toast.promise(deleteSOP.run(sop.id), {
                loading: "ກໍາລັງລຶບ...",
                success: "ລຶບ SOP ສໍາເລັດ",
                error: "ລຶບ SOP ລົ້ມເຫຼວ",
              })
            }
          />
        </div>
      </Main>
    </>
  );
}
